import { cloneElement, forwardRef, isValidElement, ReactNode } from "react";
import type {
  RadioButtonProps as ReactAriaRadioButtonProps,
  RadioGroupProps as ReactAriaRadioGroupProps,
} from "react-aria-components";
import {
  RadioButton as ReactAriaRadioButton,
  RadioField as ReactAriaRadioField,
  RadioGroup as ReactAriaRadioGroup,
} from "react-aria-components";
import { FieldError } from "react-hook-form";

import { cn } from "../utils";
import { ncButtonClasses } from "./nc-button";
import { NcFieldLayout, NcFieldLayoutProps, ncFieldLayoutVariants } from "./nc-field-layout";
import { NcIconTick } from "./nc-icons";
import { NcLabel } from "./nc-label";

type NcRadioOption = {
  label: string;
  value: string;
  description?: string;
  isDisabled?: boolean;
  // Allow extra fields for custom rendering
  [key: string]: string | boolean | number | ReactNode | undefined;
};

export interface NcRadioButtonGroupProps extends Omit<ReactAriaRadioGroupProps, "children"> {
  label: string;
  hideLabel?: boolean;
  description?: string;
  error?: FieldError;
  variant?: NcFieldLayoutProps["variant"];
  options: Array<NcRadioOption>;
  children?: ReactNode | ((option: NcRadioOption) => ReactNode);
}

const RadioButtonGroup = forwardRef<HTMLDivElement, NcRadioButtonGroupProps>(
  (
    {
      label,
      hideLabel,
      description,
      error,
      isRequired,
      children,
      variant = "horizontal",
      className,
      options,
      ...props
    },
    ref
  ) => (
    <ReactAriaRadioGroup
      data-nc="NcRadioGroup"
      ref={ref}
      className={cn(ncFieldLayoutVariants(), className)}
      {...props}
    >
      <NcLabel isRequired={isRequired} hideLabel={hideLabel}>
        {label}
      </NcLabel>
      <NcFieldLayout
        variant={variant}
        className={cn(
          "items-stretch gap-0",
          variant === "horizontal" &&
            "*:not-first-of-type:[&>label]:-ml-[2px] *:not-first-of-type:[&>label]:rounded-l-none *:not-last-of-type:[&>label]:rounded-r-none",
          variant === "vertical" &&
            "*:not-first-of-type:[&>label]:-mt-[2px] *:not-first-of-type:[&>label]:rounded-t-none *:not-last-of-type:[&>label]:rounded-b-none"
        )}
      >
        {typeof children === "function" ? (
          options.map(option => {
            const rendered = children(option);
            // If it's a valid React element, inject the key
            if (isValidElement(rendered)) {
              return cloneElement(rendered, { key: option.value });
            }
            // Otherwise, just return as is (user must handle key)
            return rendered;
          })
        ) : children ? (
          <>{children}</>
        ) : (
          options.map(option => (
            <NcRadioButton
              key={option.value}
              value={option.value}
              isDisabled={option.isDisabled}
              className={cn(
                "w-full gap-2",
                option.description && "content-start items-start justify-start py-3"
              )}
            >
              <RadioIndicator />
              <div className="grid gap-1">
                <span>{option.label}</span>
                {option.description && (
                  <span className="font-normal italic">{option.description}</span>
                )}
              </div>
            </NcRadioButton>
          ))
        )}
      </NcFieldLayout>
      {description && <NcFieldLayout.Description>{description}</NcFieldLayout.Description>}
      <NcFieldLayout.ErrorMessage>{error?.message}</NcFieldLayout.ErrorMessage>
    </ReactAriaRadioGroup>
  )
);

interface NcRadioProps extends ReactAriaRadioButtonProps {
  value: string;
  isDisabled?: boolean;
  children: ReactNode;
}

const NcRadioButton = forwardRef<HTMLLabelElement, NcRadioProps>(
  ({ children, className, value, isDisabled, ...props }, ref) => {
    return (
      // `contents` keeps the field wrapper out of the layout so the label below is the
      // actual flex/grid item, letting `className` be the single place to style this button.
      <ReactAriaRadioField
        data-nc="NcRadio"
        className="contents"
        value={value}
        isDisabled={isDisabled}
      >
        <ReactAriaRadioButton
          ref={ref}
          className={cn(
            ncButtonClasses.base,
            ncButtonClasses.variant.default,
            "group flex justify-start gap-1 text-left",
            "data-invalid:border-danger",
            "data-focused:z-1 data-hovered:z-1 data-selected:z-2",
            className
          )}
          {...props}
        >
          {children}
        </ReactAriaRadioButton>
      </ReactAriaRadioField>
    );
  }
);

const RadioIndicator = () => (
  <div
    className={cn(
      // Sizing, shape, border
      "bg-light text-main flex h-5 w-5 min-w-0 shrink-0 cursor-pointer items-center justify-center rounded-full border transition-colors duration-300",
      // Disabled
      "group-data-disabled:bg-disabled-light group-data-disabled:border-disabled",
      // Selected + disabled
      "group-data-disabled:group-data-selected:bg-disabled",
      // Invalid
      "group-data-invalid:border-danger"
    )}
  >
    <NcIconTick className="h-4 scale-0 transition-all duration-300 ease-out group-data-selected:scale-100" />
  </div>
);

export const NcRadioButtonGroup = Object.assign(RadioButtonGroup, {
  Radio: NcRadioButton,
  Indicator: RadioIndicator,
});
